import * as path from "path";

import { Express } from "express";
import { FarmConfig } from "Tools/src/CandyCLI";

import { EngineVersionUtils } from "../../../BuildTasks/EngineVersionUtils";
import { resolveEngineVersion } from "../../../BuildTasks/PublishedVersionUtils";
import { resolveFormInfo } from "../../../BuildTasks/ResolveFormInfo";
import { FormMetaContent } from "../../../BuildTasks/WriteFormMeta";
import { httpGet } from "../../../Commons/HttpServer/AttributeRouting/Methods";
import { httpType, httpUrlParam } from "../../../Commons/HttpServer/AttributeRouting/Params";
import { httpReturn } from "../../../Commons/HttpServer/AttributeRouting/Results";
import { SingletonController } from "../../../Commons/HttpServer/ExpressWrapper/SingletonController";
import { CandyVersionSpec } from "../../../BuildTasks/Version";

import { FarmDirectory } from "./Directories/FarmDirectory";

export class BuildServerFormInfoController extends SingletonController {
    private readonly allFarmConfig: FarmConfig[];
    private allFarmDirectories: FarmDirectory[];
    private readonly nightly: boolean;
    private readonly useEngineFromLocalSources: boolean;

    public constructor(allFarmConfig: FarmConfig[], useEngineFromLocalSources: boolean, nightly: boolean) {
        super();
        this.nightly = nightly;
        this.allFarmConfig = allFarmConfig;
        this.allFarmDirectories = allFarmConfig.map(i => new FarmDirectory(i.farmDirectory));
        this.useEngineFromLocalSources = useEngineFromLocalSources;
    }

    public async setupMiddleware(expressApplication: Express) {
        expressApplication.use("/force-gfvs", (_, __, next) => {
            this.resetAllFarmDirectories();
            next();
        });
    }

    private resetAllFarmDirectories() {
        this.allFarmDirectories = this.allFarmConfig.map(i => new FarmDirectory(i.farmDirectory));
    }

    @httpGet("/metas", httpReturn.PlainText)
    public async getAllFormMetas(): Promise<string> {
        return (await Promise.all(this.allFarmDirectories.map(i => i.getAllGfvs())))
            .flat()
            .map(x => `${x}.metas.json`)
            .join("\n");
    }

    @httpGet("/metas/:gfv.metas.json", httpReturn.PlainText, httpUrlParam("gfv", httpType.String))
    public async getFormMeta(gfv: string): Promise<string> {
        const formJsonFile = (await Promise.all(this.allFarmDirectories.map(i => i.getFormJsonPath(gfv))))
            .flat()
            .filter(i => i != undefined);
        if (formJsonFile[0] == undefined) {
            throw Error(`${gfv} not found in farm directories`);
        }
        const formInfo = await resolveFormInfo(formJsonFile[0], path.dirname(formJsonFile[0]));
        if (this.useEngineFromLocalSources) {
            const localEngineMetaContent: FormMetaContent = {
                engineName: "local",
                resourcesHash: "local-hash",
                useServerSideNormalization: formInfo.useServerSideNormalization,
            };
            return JSON.stringify(localEngineMetaContent, undefined, "  ");
        }
        const engineVersion: undefined | CandyVersionSpec = this.nightly
            ? { type: "pre", preId: "nightly" }
            : formInfo.engineVersion;
        if (engineVersion == undefined) {
            throw new Error("Unknown engine version! Can't return meta for this form!");
        }
        const engineSources = await resolveEngineVersion(engineVersion);
        const formMetaContent: FormMetaContent = {
            engineName:
                engineSources.type === "LocalEngineSources"
                    ? "local"
                    : EngineVersionUtils.engineVersionToName(engineSources.version),
            resourcesHash: "local-hash",
            useServerSideNormalization: formInfo.useServerSideNormalization,
        };
        if (formInfo.themes != undefined) {
            formMetaContent.themes = formInfo.themes;
        }
        return JSON.stringify(formMetaContent, undefined, "  ");
    }
}
